import React, {Suspense, useContext} from 'react'
// import ElasticsearchAPIConnector from '@elastic/search-ui-elasticsearch-connector'
import AppSearchAPIConnector from '@elastic/search-ui-app-search-connector'
import {SearchProvider, WithSearch} from '@elastic/react-search-ui'
import {useTranslation} from 'react-i18next'
import {containsKeyword} from '../../utils/utils'
import {AppContext} from '../../context/context'
import {Helmet} from 'react-helmet'

import Header from '../header'
import ResultTotal from '../results/result-total'
import Pagination from '../results/pagination'
import LoadingIndicator from '../results/loading-indicator'
import Footer from '../footer'
import BackToTopButton from './back-to-top'
import FeedbackButton from './feedback-button'
import FilterBar from './filter-bar'
import DidYouMean from './did-you-mean'

import {SearchWrapper, ResultsOverviewText, MainContainer} from './styles'
import {ScreenReaderOnly} from '../../theme/visibility'

const SearchLayout = ({children, isSearchResultsPage, generateResultView}) => {
  const {t} = useTranslation('common')
  const {state} = useContext(AppContext)

  const connector = new AppSearchAPIConnector({
    searchKey: process.env.REACT_APP_SEARCH_KEY,
    engineName: process.env.REACT_APP_ENGINE_NAME,
    endpointBase: process.env.REACT_APP_ENDPOINT_BASE,
  })

  const languageFilterList =
    state.language === 'en'
      ? ['en', 'en-US', 'en en', 'en-ca', '']
      : ['fr', 'fr-FR', 'fr fr', 'fr-ca']

  const languageFilter = {
    field: 'lang',
    values: languageFilterList,
    type: 'any',
  }

  // If query contains the word 'Form' (see list of keywords in common.json), include results from forms.mgcs.gov.on.ca.
  const containsFormKeyword = containsKeyword(
    t('formKeywords', {returnObjects: true}),
    state.query,
  )

  // Note: Filters need a placeholder value in order to work with type `none`.
  // When using Placeholder values with filters of type 'none` the filter returns all results for that filter, including results that dont have the field.
  // This is needed since not all documents have the 'Filter' (or 'Archived`) field.
  const formsFilterList = containsFormKeyword ? 'formsPlaceholder' : 'form'

  // If query contains the word 'Archived' (see list of keywords in common.json), include results that are archived
  const containsArchived = containsKeyword(
    t('archivedKeywords', {returnObjects: true}),
    state.query,
  )

  // Note: Filters need a placeholder value in order to work with type `none`.
  // When using Placeholder values with filters of type 'none` the filter returns all results for that filter, including results that dont have the field.
  // This is needed since not all documents have the 'Archived' (or 'Filter`) field.
  const archivedFilterList = [containsArchived ? 'archivedPlaceholder' : 'true']

  // Based on the filter state (which filter the user selects), we will need different configuration for the filters.
  const searchFilterConfiguration = () => {
    switch (state.filter) {
      case 'elaws':
        // The 'elaws' filter only includes documents with the field 'Filter' having the value 'elaws' in its list.
        return [
          languageFilter,
          {field: 'filter', values: ['elaws'], type: 'all'},
        ]
      case 'data':
        // The 'Data' filter only includes documents with the field 'Filter' having the value 'data' in its list.
        return [
          languageFilter,
          {field: 'filter', values: ['data'], type: 'all'},
        ]
      default:
        // The default 'Main' filter. The values of 'Filter' and 'Archived' depend on the users query.
        return [
          languageFilter,
          {
            field: 'filter',
            values: ['data', 'elaws', formsFilterList],
            type: 'none',
          },
          {field: 'archived', values: archivedFilterList, type: 'none'},
        ]
    }
  }

  return (
    <Suspense fallback="loading">
      <Helmet>
        <title>
          {`${
            state.query
              ? `${t('pageTitle')} ${state.query}`
              : t('enterSearchTerm')
          } | ontario.ca`}
        </title>
        <meta name="description" content={t('pageDescription')} />
        <script>
          {`
            // This is the configuration for the ongov/search Autosuggest NPM package.
            window.apiConfig ={
              'searchKey': "${process.env.REACT_APP_SEARCH_KEY}",
              'engineName': "${process.env.REACT_APP_ENGINE_NAME}",
              'endpointBase': "${process.env.REACT_APP_ENDPOINT_BASE}",
            }
            // This is the endpoint configuration for the search results page for EN and FR
            window.endpointConfig = {
              'en': "/search/search-results/",
              'fr': "/fr/recherche/resultats-de-recherche/"
            }`}
        </script>
      </Helmet>
      <SearchWrapper>
        <SearchProvider
          config={{
            apiConnector: connector,
            hasA11yNotifications: true,
            trackUrlState: false,
            searchQuery: {
              filters: searchFilterConfiguration(),
            },
            autocompleteQuery: {
              // Fields for 'Suggested searches'
              suggestions: {
                types: {
                  documents: {
                    fields: ['title'],
                  },
                },
              },
              // Fields for 'Suggested pages'
              results: {
                resultsPerPage: 10,
                result_fields: {
                  title: {
                    raw: {},
                  },
                  url: {
                    raw: {},
                  },
                  url_host: {
                    raw: {},
                  },
                  archived: {
                    raw: {},
                  },
                  lang: {
                    raw: {},
                  },
                },
                search_fields: {
                  title: {},
                  url: {},
                  lang: {},
                },
              },
              size: 10,
            },
          }}
        >
          <Header />
          <MainContainer>
            {isSearchResultsPage ? (
              <WithSearch
                mapContextToProps={({
                  results,
                  isLoading,
                  wasSearched,
                  ...props
                }) => ({
                  results,
                  isLoading,
                  wasSearched,
                  ...props,
                })}
              >
                {({results, isLoading}) => {
                  if (isLoading) {
                    return <LoadingIndicator />
                  }

                  if (!isLoading) {
                    return state.query ? (
                      <>
                        <FilterBar t={t} />
                        <div className="ontario-row">
                          <div className="ontario-columns ontario-small-12">
                            <ScreenReaderOnly
                              role="alert"
                              aria-live="assertive"
                            >
                              {t('resultsLoaded')}
                            </ScreenReaderOnly>

                            <DidYouMean />
                            <ResultTotal
                              results={results}
                              lang={state.language}
                              filter={state.filter}
                            />
                            {results.map((result, index) =>
                              generateResultView(result, index),
                            )}
                            <Pagination />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="ontario-row">
                        <div className="ontario-columns ontario-small-12">
                          <ResultsOverviewText>
                            {t('enterSearchTerm')}
                          </ResultsOverviewText>
                        </div>
                      </div>
                    )
                  }
                }}
              </WithSearch>
            ) : (
              {children}
            )}
            <FeedbackButton lang={state.language} query={state.query} />
            <BackToTopButton />
          </MainContainer>
          <Footer />
        </SearchProvider>
      </SearchWrapper>
    </Suspense>
  )
}

export default SearchLayout
